import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import {
  getPublicCharacterByCode,
  type CharacterContent,
  type CharacterRow,
} from "@/lib/characters.functions";
import { CharacterImage } from "@/components/CharacterImage";
import { getCharacterMeta, translateTrait } from "@/lib/character-meta";
import { PERSONAS, type CharacterCode } from "@/lib/character-personality";
import { coverFor } from "@/assets/worlds";
import { Sparkles, Heart, Compass } from "lucide-react";


type LoaderData = { character: CharacterRow | null; items: CharacterContent[] };

export const Route = createFileRoute("/characters/$code")({
  loader: async ({ params }) => {
    const data = await getPublicCharacterByCode({ data: { code: params.code } });
    if (!data.character) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const c = (loaderData as LoaderData | undefined)?.character;
    const title = c ? `${c.name_en} | ${c.name_ar} — Jozori` : "Character — Jozori";
    const desc = c
      ? `${c.bio_en ?? `Meet ${c.name_en}, one of the Jozori friends.`} · ${c.bio_ar ?? `تعرّف على ${c.name_ar} من أصدقاء جذوري.`}`
      : "Jozori character";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `https://jozori.com/characters/${c?.code ?? ""}` },
        ...(c?.avatar_url ? [{ property: "og:image", content: c.avatar_url }] : []),
      ],
      links: c
        ? [{ rel: "canonical", href: `https://jozori.com/characters/${c.code}` }]
        : [],
    };
  },
  component: CharacterDetail,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Character not found</h1>
        <Link to="/characters" className="mt-4 inline-block text-primary underline">
          Back to characters
        </Link>
      </div>
    </div>
  ),
});

const KIND_EMOJI: Record<string, string> = {
  story: "📖",
  video: "🎬",
  quiz: "❓",
  printable: "🖨️",
  activity: "✨",
  challenge: "🎯",
  coloring: "🎨",
  worksheet: "📄",
};
function kindEmoji(k: string) {
  return KIND_EMOJI[k] ?? "✨";
}

function CharacterDetail() {
  const { character, items } = Route.useLoaderData() as LoaderData;
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";
  if (!character) return null;
  const c = character;
  const meta = getCharacterMeta(c.code);
  const persona = (PERSONAS as Record<string, (typeof PERSONAS)[CharacterCode] | undefined>)[c.code];

  // Prefer rich meta; fall back to whatever the DB has.
  const ageLabel = meta ? (isAr ? meta.ageLabelAr : meta.ageLabelEn) : null;
  const tierLabel = meta ? (isAr ? meta.tierAr : meta.tierEn) : null;
  const bio =
    (meta ? (isAr ? meta.bioAr : meta.bioEn) : null) ??
    (isAr ? c.bio_ar : c.bio_en);
  const traits =
    meta && (isAr ? meta.traitsAr : meta.traitsEn).length > 0
      ? isAr
        ? meta.traitsAr
        : meta.traitsEn
      : c.traits;
  const values = meta ? (isAr ? meta.valuesAr : meta.valuesEn) : [];
  const hi = persona ? (isAr ? persona.hiAr : persona.hiEn) : null;

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/characters"
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
            >
              {isAr ? "كل الشخصيات" : "All characters"}
            </Link>
            <button
              onClick={toggle}
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
            >
              {isAr ? "English" : "العربية"}
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, ${c.color}33, ${c.color}11 60%, transparent)` }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 size-96 rounded-full blur-3xl opacity-40"
          style={{ background: c.color }}
        />
        <div className="relative mx-auto max-w-5xl px-4 py-14 md:py-20 grid md:grid-cols-[auto,1fr] gap-8 md:gap-12 items-center">
          {/* Big portrait */}
          <div
            className="mx-auto md:mx-0 size-44 md:size-56 rounded-[2rem] overflow-hidden ring-4 ring-card"
            style={{
              background: `linear-gradient(135deg, ${c.color}44, ${c.color}88)`,
              boxShadow: `0 24px 60px -16px ${c.color}88`,
            }}
          >
            <CharacterImage
              code={c.code}
              fallbackUrl={c.avatar_url}
              alt={isAr ? c.name_ar : c.name_en}
              className="size-full object-cover"
              loading="eager"
              sizes="(min-width: 768px) 224px, 176px"
            />
          </div>

          {/* Identity */}
          <div className={`text-center md:${isAr ? "text-right" : "text-left"}`}>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
              {ageLabel && (
                <span
                  className="rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest"
                  style={{ background: `${c.color}22`, color: c.color }}
                >
                  {ageLabel}
                </span>
              )}
              {tierLabel && (
                <span className="rounded-full border border-clay/40 bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-foreground/70">
                  {tierLabel}
                </span>
              )}
            </div>
            <h1
              className={`text-5xl md:text-6xl font-black tracking-tight ${isAr ? "font-display-ar" : "font-display-en"}`}
              style={{ color: c.color }}
            >
              {isAr ? c.name_ar : c.name_en}
            </h1>
            {hi && (
              <p
                className={`mt-4 text-lg md:text-xl text-foreground/80 ${isAr ? "font-display-ar" : ""}`}
              >
                {hi}
              </p>
            )}
            {traits.length > 0 && (
              <div className={`mt-5 flex flex-wrap gap-2 justify-center md:${isAr ? "justify-end" : "justify-start"}`}>
                {traits.map((t) => (
                  <span
                    key={t}
                    className="rounded-full px-3 py-1 text-xs font-bold bg-card border border-clay/40"
                  >
                    {translateTrait(t, isAr)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ABOUT + VALUES */}
      <section className="mx-auto max-w-5xl px-4 py-12 grid md:grid-cols-3 gap-6">
        {bio && (
          <article className="md:col-span-2 rounded-3xl border border-clay/30 bg-card p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3 text-foreground/60">
              <Sparkles className="size-4" aria-hidden="true" style={{ color: c.color }} />
              <p className="text-[11px] font-black uppercase tracking-widest">
                {isAr ? `عن ${c.name_ar}` : `About ${c.name_en}`}
              </p>
            </div>
            <p
              className={`text-base md:text-lg leading-relaxed text-foreground/80 ${isAr ? "font-display-ar text-lg md:text-xl" : ""}`}
              dir={isAr ? "rtl" : "ltr"}
            >
              {bio}
            </p>
          </article>
        )}

        {values.length > 0 && (
          <aside
            className="rounded-3xl p-6 md:p-8 text-foreground"
            style={{ background: `linear-gradient(160deg, ${c.color}22, ${c.color}11)` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Heart className="size-4" aria-hidden="true" style={{ color: c.color }} />
              <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: c.color }}>
                {isAr ? "القيم التي يحملها" : "Values"}
              </p>
            </div>
            <ul className="space-y-2">
              {values.map((v) => (
                <li
                  key={v}
                  className={`flex items-center gap-2 text-sm md:text-base font-bold ${isAr ? "font-display-ar text-lg" : ""}`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-block size-1.5 rounded-full"
                    style={{ background: c.color }}
                  />
                  {v}
                </li>
              ))}
            </ul>
          </aside>
        )}
      </section>

      {/* RELATED CONTENT */}
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="flex items-center gap-2 mb-6">
          <Compass className="size-5" aria-hidden="true" style={{ color: c.color }} />
          <h2
            className={`text-2xl md:text-3xl font-black ${isAr ? "font-display-ar" : "font-display-en"}`}
          >
            {isAr ? `قصص وأنشطة مع ${c.name_ar}` : `Stories and activities with ${c.name_en}`}
          </h2>
        </div>
        {items.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-clay/40 bg-card p-8 text-center text-foreground/60">
            <p className={isAr ? "font-display-ar text-lg" : ""}>
              {isAr
                ? `قصص ${c.name_ar} وأنشطته قادمة قريبًا — تابعنا.`
                : `${c.name_en}'s stories and activities are coming soon — stay tuned.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => {
              const itemCover = coverFor({ cover_url: it.cover_url, world_id: it.world_id, age_group: it.age_group, slug: it.slug, character_code: c.code, character_avatar: c.avatar_url });
              return (
              <Link
                key={it.id}
                to="/app/watch/$id"
                params={{ id: it.id }}
                className="rounded-3xl border-2 border-clay/30 bg-card overflow-hidden hover:border-primary hover:-translate-y-1 transition-all"
              >
                {itemCover ? (
                  <img
                    src={itemCover}
                    alt=""
                    loading="lazy"
                    className="aspect-[3/2] w-full object-cover"
                  />
                ) : (
                  <div
                    className="aspect-[3/2] w-full"
                    style={{ background: `linear-gradient(135deg, ${c.color}22, ${c.color}44)` }}
                  />
                )}
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/50">
                    {kindEmoji(it.kind)} {it.kind} · {it.age_group}
                  </p>
                  <h3
                    className={`mt-1 font-bold leading-tight line-clamp-2 ${
                      isAr ? "font-display-ar" : "font-display-en"
                    }`}
                  >
                    {isAr ? it.title_ar : it.title_en}
                  </h3>
                </div>
              </Link>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/app"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-primary-foreground hover:opacity-90 transition-opacity"
            style={{ background: c.color }}
          >
            {isAr ? `العب مع ${c.name_ar} في التطبيق` : `Play with ${c.name_en} in the app`}
          </Link>
        </div>
      </section>
    </div>
  );
}
